import { IShader } from './IShader';
import { ShaderProgram } from './ShaderProgram';
import { Matrix4 } from './Matrix4';
import { VertexElementUsage } from './VertexElementUsage';
import { Filter } from './Filter';
import { ImageAdjustParams } from './ImageAdjustParams';
import { MaskParams } from './MaskParams';
import { Matrix3 } from './Matrix3';

export class Shader implements IShader {
  program: ShaderProgram;

  static readonly DiffuseTextureUnit = 0;

  private _projection: Matrix4;

  private _uPMatrix: any;

  private _brLocation: any;
  private _mtxLocation: any;

  private _clrOffLocation: any;

  private _uTransform: any;

  private _uMaskMatrix: any[] = new Array(2);
  private _maskTextCoords: any[] = new Array(2);
  private _maskTextCoordsScale: any[] = new Array(2);

  private _uFilterKind: any;
  private _uFilterTime: any;
  private _uFilterIntensity: any;
  private _uFilterTintColor: any;
  private _uFilterPixelSize: any;
  private _uFilterCenter: any;
  private _uFilterRadius: any;
  private _uFilterStrength: any;
  private _uFilterAngle: any;
  private _uFilterRedOffset: any;
  private _uFilterGreenOffset: any;
  private _uFilterBlueOffset: any;
  private _uFilterSepia: any;

  constructor(program: ShaderProgram) {
    this.program = program;
    program.bindAttribute(VertexElementUsage.Position, 'av_Pos');
    program.bindAttribute(VertexElementUsage.TexCoord, 'av_TexCoord');
    program.bindAttribute(VertexElementUsage.Color, 'av_Color');

    this._resolveAttributes();
  }

  dispose(): void {
    this.program.dispose();
  }

  private _resolveAttributes(): void {
    this.program.link();

    this._uPMatrix = this.program.getUniformLocation('uv_Projection');

    // Color Adjust
    this._brLocation = this.program.getUniformLocation('br');
    this._mtxLocation = this.program.getUniformLocation('hue');

    this._clrOffLocation = this.program.getUniformLocation('clrOffset');

    this._uTransform = this.program.getUniformLocation('um_Transform');

    const diffuse = this.program.getUniformLocation('us_0');
    if (diffuse) {
      this.program.uniform1i(diffuse, Shader.DiffuseTextureUnit);
    }

    this._addMask(0);
    this._addMask(1);

    this._uFilterKind = this.program.getUniformLocation('u_filterKind');
    this._uFilterTime = this.program.getUniformLocation('u_filterTime');
    this._uFilterIntensity = this.program.getUniformLocation('u_filterIntensity');
    this._uFilterTintColor = this.program.getUniformLocation('u_filterTintColor');
    this._uFilterPixelSize = this.program.getUniformLocation('u_filterPixelSize');
    this._uFilterCenter = this.program.getUniformLocation('u_filterCenter');
    this._uFilterRadius = this.program.getUniformLocation('u_filterRadius');
    this._uFilterStrength = this.program.getUniformLocation('u_filterStrength');
    this._uFilterAngle = this.program.getUniformLocation('u_filterAngle');
    this._uFilterRedOffset = this.program.getUniformLocation('u_filterRedOffset');
    this._uFilterGreenOffset = this.program.getUniformLocation('u_filterGreenOffset');
    this._uFilterBlueOffset = this.program.getUniformLocation('u_filterBlueOffset');
    this._uFilterSepia = this.program.getUniformLocation('u_filterSepia');
  }

  private _addMask(index: number): void {
    const p = index + 1;
    this._uMaskMatrix[index] = this.program.getUniformLocation(`maskMatrix${p}`);

    const mask = this.program.getUniformLocation(`us_M${p}`);
    if (mask) {
      this.program.uniform1i(mask, p);
    }
    this._maskTextCoords[index] = this.program.getUniformLocation(`maskTexCoord${p}`);
    this._maskTextCoordsScale[index] = this.program.getUniformLocation(`maskTexCoordScale${p}`);
  }

  setProjection(value: Matrix4): void {
    if (this._projection !== value) {
      this.program.uniform4f(
        this._uPMatrix,
        value.data[0],
        value.data[5],
        value.data[12],
        value.data[13]
      );
      this._projection = value;
    }
  }

  setImageAdjust(imageAdjust: ImageAdjustParams): void {
    if (this._brLocation && this._mtxLocation) {
      this.program.uniformMatrix3fv(this._mtxLocation, imageAdjust.matrix);
      this.program.uniform3f(
        this._brLocation,
        imageAdjust.offset,
        imageAdjust.offset,
        imageAdjust.offset
      );
    }

    if (this._clrOffLocation) {
      this.program.uniform3f(
        this._clrOffLocation,
        imageAdjust.colorOffset.r,
        imageAdjust.colorOffset.g,
        imageAdjust.colorOffset.b
      );
    }
  }

  setMask(index: number, params: MaskParams): void {
    if (this._uMaskMatrix[index]) {
      this.program.uniformMatrix3fv(this._uMaskMatrix[index], params.matrix.toList());
      this.program.uniform2f(this._maskTextCoords[index], params.texCoords.x, params.texCoords.y);
      this.program.uniform2f(
        this._maskTextCoordsScale[index],
        params.texCoordsScale.x,
        params.texCoordsScale.y
      );
    }
  }

  setTransform(params: Matrix3): void {
    if (this._uTransform) {
      this.program.uniformMatrix3fv(this._uTransform, params.toList());
    }
  }

  setFilter(filter: Filter): void {
    if (this._uFilterKind) {
      this.program.uniform1i(this._uFilterKind, filter.kind);
    }
    if (this._uFilterTime) {
      this.program.uniform1f(this._uFilterTime, filter.time);
    }
    if (this._uFilterIntensity) {
      this.program.uniform1f(this._uFilterIntensity, filter.intensity);
    }
    if (this._uFilterTintColor) {
      this.program.uniform4f(
        this._uFilterTintColor,
        filter.tintColor.r,
        filter.tintColor.g,
        filter.tintColor.b,
        filter.tintColor.a
      );
    }
    if (this._uFilterPixelSize) {
      this.program.uniform1f(this._uFilterPixelSize, filter.pixelSize);
    }
    if (this._uFilterCenter) {
      this.program.uniform2f(this._uFilterCenter, filter.center.x, filter.center.y);
    }
    if (this._uFilterRadius) {
      this.program.uniform1f(this._uFilterRadius, filter.radius);
    }
    if (this._uFilterStrength) {
      this.program.uniform1f(this._uFilterStrength, filter.strength);
    }
    if (this._uFilterAngle) {
      this.program.uniform1f(this._uFilterAngle, filter.angle);
    }
    if (this._uFilterRedOffset) {
      this.program.uniform2f(this._uFilterRedOffset, filter.redOffset.x, filter.redOffset.y);
    }
    if (this._uFilterGreenOffset) {
      this.program.uniform2f(this._uFilterGreenOffset, filter.greenOffset.x, filter.greenOffset.y);
    }
    if (this._uFilterBlueOffset) {
      this.program.uniform2f(this._uFilterBlueOffset, filter.blueOffset.x, filter.blueOffset.y);
    }
    if (this._uFilterSepia) {
      this.program.uniform1f(this._uFilterSepia, filter.sepia);
    }
  }
}
